import { useLocation } from "react-router-dom";
import type { ReactNode } from "react"
import { Box } from "@mui/material"

/**
 * Subtle route-change entrance used in the authenticated layout.
 *
 * The outer box is keyed by pathname, so React unmounts + remounts it on every
 * client-side navigation. That remount re-triggers the `riseFade` keyframe — a
 * 220ms 8px rise + fade. Intentionally small: enough to say "this is a new
 * view" without drawing attention to the motion. The keyframe is defined inline
 * in `sx` so it is fully self-contained (no dependency on globals.css).
 *
 * `prefers-reduced-motion` collapses this to a no-op via the media query in the
 * `sx` below, so we don't need a JS branch here.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  return (
    <Box
      key={pathname}
      sx={{
        "@keyframes riseFade": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        animation: "riseFade 220ms ease-out",
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
        },
      }}
    >
      {children}
    </Box>
  )
}
